import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Tag, Search, Loader2, RefreshCw, X,
  ChefHat, Beef, Leaf, Milk, ShoppingBasket, LayoutGrid, AlertCircle, Plus, Check, ShoppingCart,
  Sparkles, Clock, Flame, Globe, ChevronRight, DollarSign, ShoppingBag, Lightbulb, Users,
  Bookmark, BookmarkCheck,
} from 'lucide-react';
import { useAppStore, FlyerItem } from '@/hooks/useAppStore';
import { ROUTE_PATHS, mealTypeIcon } from '@/lib/index';
import { useAuth } from '@/hooks/useAuth';
import UserMenu from '@/components/UserMenu';
import { useSyncedRecipes } from '@/hooks/useSyncedRecipes';

const EDGE_FUNCTION_URL = 'https://pgjncthsfxsylmvkgibx.supabase.co/functions/v1/meal-plan';
const ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBnam5jdGhzZnhzeWxtdmtnaWJ4Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzcxMDEwMTEsImV4cCI6MjA5MjY3NzAxMX0.PDtIhTOuZNnwOqNkdOD8eTC6SCXdlPT5H7vrcU30UXk';

interface DealItem { name: string; price: number; }
interface GroupedDeals { [cat: string]: DealItem[]; }
interface MealIdea {
  name: string;
  tagline: string;
  cook_time: string;
  method: string;
  cuisine: string;
  difficulty: 'Easy' | 'Medium' | 'Weekend Project';
}
interface QuickRecipeIngredient {
  name: string; qty: string; est_price: number; on_sale: boolean;
}
interface QuickRecipe {
  name: string; serves: number; cook_time: string;
  est_cost: number; cost_per_serving: number;
  ingredients: QuickRecipeIngredient[];
  instructions: string[];
  tip?: string;
}

// ─── Unit-price parser ────────────────────────────────────────────────────────
type UnitPriceResult = { display: string; per: 'kg' | 'lb' } | null;

function parseUnitPrice(name: string, price: number): UnitPriceResult {
  if (price <= 0) return null;
  const n = name.toLowerCase();

  // explicit per-kg / per-lb labels in the name mean the listed price IS the unit price
  if (/\/\s*kg|per\s+kg|\bpkg\b/.test(n)) return { display: `$${price.toFixed(2)}/kg`, per: 'kg' };
  if (/\/\s*lb|per\s+lb/.test(n)) return { display: `$${price.toFixed(2)}/lb`, per: 'lb' };

  // weight in name → calculate unit price
  const gMatch = n.match(/(\d+(?:\.\d+)?)\s*g\b/);
  if (gMatch) {
    const grams = parseFloat(gMatch[1]);
    if (grams >= 100 && grams < 5000) {
      const perKg = price / (grams / 1000);
      return { display: `$${perKg.toFixed(2)}/kg`, per: 'kg' };
    }
  }
  const kgMatch = n.match(/(\d+(?:\.\d+)?)\s*kg\b/);
  if (kgMatch) {
    const kgs = parseFloat(kgMatch[1]);
    if (kgs > 0 && kgs <= 10) {
      const perKg = price / kgs;
      return { display: `$${perKg.toFixed(2)}/kg`, per: 'kg' };
    }
  }
  const lbMatch = n.match(/(\d+(?:\.\d+)?)\s*lb\b/);
  if (lbMatch) {
    const lbs = parseFloat(lbMatch[1]);
    if (lbs > 0 && lbs <= 20) {
      const perLb = price / lbs;
      return { display: `$${perLb.toFixed(2)}/lb`, per: 'lb' };
    }
  }
  return null;
}

// ─── Category metadata ────────────────────────────────────────────────────────
const CAT_META: Record<string, { icon: typeof Tag; color: string; bg: string; border: string }> = {
  'All':          { icon: LayoutGrid,    color: 'text-white',         bg: 'bg-white/10',         border: 'border-white/20' },
  'Proteins':     { icon: Beef,          color: 'text-red-400',       bg: 'bg-red-500/10',       border: 'border-red-500/20' },
  'Produce':      { icon: Leaf,          color: 'text-green-400',     bg: 'bg-green-500/10',     border: 'border-green-500/20' },
  'Dairy & Eggs': { icon: Milk,          color: 'text-yellow-400',    bg: 'bg-yellow-500/10',    border: 'border-yellow-500/20' },
  'Pantry':       { icon: ShoppingBasket,color: 'text-orange-400',    bg: 'bg-orange-500/10',    border: 'border-orange-500/20' },
  'Other':        { icon: Tag,           color: 'text-purple-400',    bg: 'bg-purple-500/10',    border: 'border-purple-500/20' },
};

const PRICE_COLORS = [
  { max: 2,   cls: 'text-green-400 bg-green-500/15 border-green-500/25' },
  { max: 5,   cls: 'text-emerald-400 bg-emerald-500/15 border-emerald-500/25' },
  { max: 10,  cls: 'text-yellow-400 bg-yellow-500/15 border-yellow-500/25' },
  { max: 999, cls: 'text-orange-400 bg-orange-500/15 border-orange-500/25' },
];
function priceColor(price: number) {
  return PRICE_COLORS.find(p => price <= p.max)?.cls ?? PRICE_COLORS[PRICE_COLORS.length - 1].cls;
}

const DIFFICULTY_META: Record<string, { cls: string; emoji: string }> = {
  'Easy':             { cls: 'text-green-400 bg-green-500/15',   emoji: '🟢' },
  'Medium':           { cls: 'text-yellow-400 bg-yellow-500/15', emoji: '🟡' },
  'Weekend Project':  { cls: 'text-orange-400 bg-orange-500/15', emoji: '🔴' },
};

// ─── Meal Ideas Drawer ────────────────────────────────────────────────────────
interface SelectedProtein { name: string; price: number; cat: string; unitPrice: UnitPriceResult; }

function MealIdeasDrawer({
  protein, storeName, onClose, onBuildPlan,
}: {
  protein: SelectedProtein; storeName: string; onClose: () => void; onBuildPlan: () => void;
}) {
  const { addFlyerItem, flyerItems, savedRecipes } = useAppStore();
  const { saveRecipe, unsaveRecipe, isRecipeSaved } = useSyncedRecipes();
  const storeName_ref = storeName; // capture for save
  // Track saved names reactively so buttons update immediately
  const savedRecipeNames = new Set(savedRecipes.map(r => r.name));
  const [ideas, setIdeas] = useState<MealIdea[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fetched, setFetched] = useState(false);
  // Per-card recipe state
  const [recipeLoading, setRecipeLoading] = useState<number | null>(null);
  const [recipes, setRecipes] = useState<Record<number, QuickRecipe>>({});
  const [openCard, setOpenCard] = useState<number | null>(null);
  const [addedToList, setAddedToList] = useState<number | null>(null);

  const fetchIdeas = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(EDGE_FUNCTION_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${ANON_KEY}`, 'apikey': ANON_KEY },
        body: JSON.stringify({ action: 'meal_ideas', proteinName: protein.name, proteinPrice: protein.price, unitLabel: protein.unitPrice?.display ?? '' }),
      });
      const data = await res.json() as { ideas?: MealIdea[]; error?: string };
      if (data.error) throw new Error(data.error);
      setIdeas(data.ideas ?? []);
      setFetched(true);
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not load ideas'); }
    finally { setLoading(false); }
  }, [protein]);

  const fetchRecipe = useCallback(async (idea: MealIdea, idx: number) => {
    if (recipes[idx]) { setOpenCard(idx); return; }
    setRecipeLoading(idx); setOpenCard(idx);
    try {
      const res = await fetch(EDGE_FUNCTION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${ANON_KEY}`, 'apikey': ANON_KEY },
        body: JSON.stringify({ action: 'quick_recipe', recipeName: idea.name, proteinName: protein.name, proteinPrice: protein.price, cuisine: idea.cuisine, method: idea.method }),
      });
      const data = await res.json() as { recipe?: QuickRecipe; error?: string };
      if (data.error) throw new Error(data.error);
      if (data.recipe) setRecipes(prev => ({ ...prev, [idx]: data.recipe! }));
    } catch (e) { console.error('quick_recipe failed:', e); }
    finally { setRecipeLoading(null); }
  }, [protein, recipes]);

  const handleSaveRecipe = (recipe: QuickRecipe, idea: MealIdea) => {
    const mealId = `flyer-${recipe.name.replace(/\s+/g,'-').toLowerCase()}`;
    const meal = {
      id: mealId,
      name: recipe.name,
      tagline: idea.tagline,
      meal_type: 'Dinner',
      day: 'Any Day',
      serves: recipe.serves ?? 4,
      cook_time: recipe.cook_time,
      method: idea.method,
      ingredients: recipe.ingredients.map(i => ({
        name: i.name, qty: i.qty,
        price: i.est_price ?? 0,
        on_sale: i.on_sale ?? false,
      })),
      total_cost: recipe.est_cost ?? 0,
      per_serving: recipe.cost_per_serving ?? 0,
      instructions: recipe.instructions ?? [],
      tip: recipe.tip ?? '',
      sides: [] as string[],
    };
    if (savedRecipeNames.has(recipe.name)) {
      unsaveRecipe(mealId);
    } else {
      saveRecipe(meal, storeName_ref);
    }
  };

  const handleAddToList = (idea: MealIdea, idx: number) => {
    addFlyerItem({ name: protein.name, price: protein.price, cat: 'Proteins' } as FlyerItem);
    setAddedToList(idx);
    setTimeout(() => setAddedToList(null), 2000);
  };

  // Estimate cost from protein price + ~$8 pantry
  const estCost = (proteinPrice: number) => {
    const base = proteinPrice > 0 ? proteinPrice + 8.50 : 14.00;
    return `~$${(base / 4).toFixed(2)}/srv · ~$${base.toFixed(2)} total`;
  };

  return (
    <>
      <motion.div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose} />
      <motion.div
        className="fixed bottom-0 left-0 right-0 z-50 max-w-lg mx-auto bg-card rounded-t-3xl border-t border-border shadow-2xl"
        style={{ maxHeight: '90vh', overflowY: 'auto' }}
        initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 300 }}
      >
        {/* Header */}
        <div className="sticky top-0 bg-card rounded-t-3xl px-5 pt-4 pb-3 border-b border-border z-10">
          <div className="w-10 h-1 bg-muted rounded-full mx-auto mb-4" />
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">On Sale · {storeName}</p>
              <h2 className="font-black text-foreground text-base leading-tight line-clamp-2">{protein.name}</h2>
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                {protein.price > 0 && (
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg border text-xs font-black ${priceColor(protein.price)}`}>
                    <Tag className="w-2.5 h-2.5" />${protein.price.toFixed(2)}
                  </span>
                )}
                {protein.unitPrice && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg border text-xs font-bold text-teal-400 bg-teal-500/10 border-teal-500/25">
                    {protein.unitPrice.display}
                  </span>
                )}
                {/* Add protein to list from header */}
                <button
                  onClick={() => { addFlyerItem({ name: protein.name, price: protein.price, cat: 'Proteins' } as FlyerItem); }}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg border text-[10px] font-bold transition-all ${
                    flyerItems.some(f => f.name === protein.name)
                      ? 'text-primary bg-primary/10 border-primary/25'
                      : 'text-muted-foreground bg-muted border-border hover:border-primary/40 hover:text-primary'
                  }`}>
                  {flyerItems.some(f => f.name === protein.name) ? <Check className="w-2.5 h-2.5" /> : <ShoppingBag className="w-2.5 h-2.5" />}
                  {flyerItems.some(f => f.name === protein.name) ? 'In list' : 'Add to list'}
                </button>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-xl bg-muted flex items-center justify-center shrink-0">
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
        </div>

        <div className="px-5 py-5">
          {/* Get ideas CTA */}
          {!fetched && !loading && (
            <button onClick={fetchIdeas}
              className="w-full py-3.5 rounded-2xl bg-primary text-white font-bold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity shadow-lg shadow-primary/30">
              <Sparkles className="w-4 h-4" /> ✨ Get 5 Meal Ideas
            </button>
          )}

          {/* Loading ideas */}
          {loading && (
            <div className="flex flex-col items-center py-10 gap-4">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 border border-primary/25 flex items-center justify-center">
                <Loader2 className="w-7 h-7 text-primary animate-spin" />
              </div>
              <div className="text-center">
                <p className="font-semibold text-foreground text-sm">Generating meal ideas…</p>
                <p className="text-xs text-muted-foreground mt-1">AI is finding the best dishes for this protein</p>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="text-center py-8">
              <p className="text-red-400 text-sm font-medium mb-3">{error}</p>
              <button onClick={fetchIdeas} className="text-sm text-primary font-semibold">Try Again</button>
            </div>
          )}

          {/* Ideas list */}
          {fetched && !loading && ideas.length > 0 && (
            <div className="space-y-3">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">
                Tap a card to see the full recipe
              </p>

              {ideas.map((idea, i) => {
                const diff = DIFFICULTY_META[idea.difficulty] ?? DIFFICULTY_META['Easy'];
                const recipe = recipes[i];
                const isOpen = openCard === i;
                const isLoadingRecipe = recipeLoading === i;
                const isAdded = addedToList === i;
                const inList = flyerItems.some(f => f.name === protein.name);

                return (
                  <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.06 }}
                    className={`rounded-2xl border overflow-hidden transition-all ${isOpen ? 'border-primary/40 shadow-md shadow-primary/10' : 'border-border bg-muted/30'}`}
                  >
                    {/* Card header — tap to expand */}
                    <button
                      onClick={() => isOpen ? setOpenCard(null) : fetchRecipe(idea, i)}
                      className="w-full text-left p-4"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-foreground text-sm leading-snug">{idea.name}</h3>
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{idea.tagline}</p>
                        </div>
                        <span className={`shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full ${diff.cls}`}>
                          {idea.difficulty}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                        <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                          <Clock className="w-3 h-3" />{idea.cook_time}
                        </span>
                        <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                          <Flame className="w-3 h-3" />{idea.method}
                        </span>
                        <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                          <Globe className="w-3 h-3" />{idea.cuisine}
                        </span>
                        {/* Estimated cost badge — always visible */}
                        <span className="flex items-center gap-1 text-[11px] font-bold text-primary ml-auto">
                          <DollarSign className="w-3 h-3" />
                          {recipe ? `~$${recipe.cost_per_serving.toFixed(2)}/srv` : estCost(protein.price).split(' · ')[0]}
                        </span>
                      </div>
                    </button>

                    {/* Expanded: loading recipe */}
                    {isOpen && isLoadingRecipe && (
                      <div className="border-t border-border/50 px-4 py-5 flex items-center justify-center gap-3">
                        <Loader2 className="w-5 h-5 text-primary animate-spin" />
                        <span className="text-sm text-muted-foreground">Building recipe…</span>
                      </div>
                    )}

                    {/* Expanded: full recipe */}
                    <AnimatePresence>
                      {isOpen && !isLoadingRecipe && recipe && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }}
                          className="overflow-hidden"
                        >
                          <div className="border-t border-border/50 px-4 pb-4 pt-3 space-y-4 bg-card">
                            {/* Cost summary bar */}
                            <div className="flex items-center justify-between rounded-xl bg-primary/8 border border-primary/20 px-3 py-2">
                              <div className="flex items-center gap-1.5">
                                <Users className="w-3.5 h-3.5 text-primary" />
                                <span className="text-xs text-muted-foreground">Serves {recipe.serves ?? 4}</span>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <Clock className="w-3.5 h-3.5 text-primary" />
                                <span className="text-xs text-muted-foreground">{recipe.cook_time}</span>
                              </div>
                              <div className="text-xs font-black text-primary">
                                ~${recipe.cost_per_serving.toFixed(2)}/srv
                              </div>
                              <div className="text-xs font-bold text-muted-foreground">
                                ~${recipe.est_cost.toFixed(2)} total
                              </div>
                            </div>

                            {/* Ingredients */}
                            <div>
                              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Ingredients</p>
                              <div className="space-y-1.5">
                                {recipe.ingredients.map((ing, j) => (
                                  <div key={j} className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-1.5 min-w-0">
                                      {ing.on_sale && <span className="text-[10px] text-primary font-bold shrink-0">🏷</span>}
                                      <span className={`text-xs leading-snug ${ing.on_sale ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
                                        {ing.qty} {ing.name}
                                      </span>
                                    </div>
                                    {ing.est_price > 0 && (
                                      <span className="text-[10px] font-bold text-muted-foreground shrink-0">
                                        ~${ing.est_price.toFixed(2)}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Instructions */}
                            <div>
                              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Instructions</p>
                              <ol className="space-y-2">
                                {recipe.instructions.map((step, j) => (
                                  <li key={j} className="flex gap-2.5">
                                    <span className="shrink-0 w-5 h-5 rounded-full bg-primary/15 text-primary text-[10px] font-bold flex items-center justify-center mt-0.5">
                                      {j + 1}
                                    </span>
                                    <p className="text-xs text-foreground leading-relaxed">{step}</p>
                                  </li>
                                ))}
                              </ol>
                            </div>

                            {/* Pro tip */}
                            {recipe.tip && (
                              <div className="flex gap-2 bg-amber-500/8 border border-amber-500/20 rounded-xl px-3 py-2.5">
                                <Lightbulb className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
                                <p className="text-xs text-foreground leading-relaxed">{recipe.tip}</p>
                              </div>
                            )}

                            {/* Save + Add to list row */}
                            <div className="flex gap-2">
                              {/* Save to My Recipes */}
                              {(() => {
                                const isSaved = savedRecipeNames.has(recipe.name);
                                return (
                                  <button
                                    onClick={() => handleSaveRecipe(recipe, idea)}
                                    className={`flex-1 py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all border ${
                                      isSaved
                                        ? 'bg-primary/10 text-primary border-primary/25'
                                        : 'bg-muted text-foreground border-border hover:border-primary/40 hover:text-primary'
                                    }`}>
                                    {isSaved ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
                                    {isSaved ? 'Saved' : 'Save Recipe'}
                                  </button>
                                );
                              })()}
                              {/* Add protein to list */}
                              <button
                                onClick={() => handleAddToList(idea, i)}
                                className={`flex-1 py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all ${
                                  inList || isAdded
                                    ? 'bg-primary/10 text-primary border border-primary/25'
                                    : 'bg-primary text-white shadow-lg shadow-primary/30 hover:opacity-90'
                                }`}>
                                {inList || isAdded
                                  ? <><Check className="w-4 h-4" /> In list</>
                                  : <><ShoppingBag className="w-4 h-4" /> Add to List</>
                                }
                              </button>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}

              {/* Build full plan CTA */}
              <div className="mt-4 rounded-2xl border border-primary/30 bg-primary/8 p-5 text-center">
                <ChefHat className="w-7 h-7 text-primary mx-auto mb-2" />
                <p className="font-black text-foreground text-sm mb-1">Want a full meal plan built around this?</p>
                <p className="text-xs text-muted-foreground mb-4">
                  AI builds complete recipes + shopping list from all this week's deals at {storeName}.
                </p>
                <button onClick={onBuildPlan}
                  className="inline-flex items-center gap-2 bg-primary text-white font-bold text-sm px-6 py-3 rounded-xl hover:opacity-90 transition-opacity shadow-lg shadow-primary/30 w-full justify-center">
                  Build Full Meal Plan <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function FlyerDealsPage() {
  const navigate = useNavigate();
  const { postalCode, selectedStore, flyerItems, addFlyerItem, removeFlyerItem } = useAppStore();
  const { setShowAuthModal: _auth } = useAuth(); void _auth;
  const [grouped,    setGrouped]    = useState<GroupedDeals>({});
  const [totalDeals, setTotalDeals] = useState(0);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [activeTab,  setActiveTab]  = useState('All');
  const [search,     setSearch]     = useState('');

  // Meal ideas drawer
  const [drawerProtein, setDrawerProtein] = useState<SelectedProtein | null>(null);

  const storeName = selectedStore?.name ?? '';

  const fetchFlyer = async () => {
    if (!postalCode || !storeName) {
      // No store in state — send user to pick one first
      navigate(ROUTE_PATHS.HOME);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res  = await fetch(EDGE_FUNCTION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${ANON_KEY}`, 'apikey': ANON_KEY },
        body: JSON.stringify({ action: 'flyer', postalCode, merchant: storeName }),
      });
      const data = await res.json() as { grouped?: GroupedDeals; totalDeals?: number; error?: string };
      if (data.error) throw new Error(data.error);
      setGrouped(data.grouped ?? {});
      setTotalDeals(data.totalDeals ?? 0);
      setActiveTab('All');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load flyer deals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchFlyer(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const allItems = useMemo(() =>
    Object.entries(grouped).flatMap(([cat, items]) =>
      items.map(i => ({ ...i, cat }))
    ), [grouped]);

  const displayItems = useMemo(() => {
    const base = activeTab === 'All'
      ? allItems
      : (grouped[activeTab] ?? []).map(i => ({ ...i, cat: activeTab }));
    if (!search.trim()) return base;
    const q = search.toLowerCase();
    return base.filter(i => i.name.toLowerCase().includes(q));
  }, [activeTab, allItems, grouped, search]);

  const tabs = ['All', ...Object.keys(CAT_META).filter(k => k !== 'All' && (grouped[k]?.length ?? 0) > 0)];

  return (
    <div className="flex flex-col bg-background overflow-hidden" style={{ paddingTop: '48px', height: '100dvh' }}>

      {/* Header */}
      <div className="shrink-0 bg-background/95 backdrop-blur border-b border-border z-30">
        <div className="container mx-auto max-w-4xl px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-xl bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors shrink-0">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="font-black text-foreground text-base leading-tight truncate">
              This Week's Flyer
            </h1>
            <p className="text-xs text-muted-foreground truncate">
              {storeName}{totalDeals > 0 ? ` · ${totalDeals} deals` : ''}
            </p>
          </div>
          {flyerItems.length > 0 && (
            <button
              onClick={() => navigate(ROUTE_PATHS.GROCERY)}
              className="flex items-center gap-1.5 bg-primary text-primary-foreground text-xs font-bold px-3 py-1.5 rounded-full hover:opacity-90 transition-opacity shrink-0">
              <ShoppingCart className="w-3.5 h-3.5" />
              {flyerItems.length} in list
            </button>
          )}
          <UserMenu />
          <button onClick={fetchFlyer} disabled={loading}
            className="w-9 h-9 rounded-xl bg-muted hover:bg-primary/20 flex items-center justify-center transition-colors shrink-0">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Search bar */}
        {!loading && totalDeals > 0 && (
          <div className="container mx-auto max-w-4xl px-4 pb-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search deals…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full h-10 pl-9 pr-4 rounded-xl bg-muted border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
              />
            </div>
          </div>
        )}

        {/* Category tabs */}
        {!loading && tabs.length > 1 && (
          <div className="overflow-x-auto scrollbar-hide">
            <div className="container mx-auto max-w-4xl px-4 pb-3">
              <div className="flex gap-2 w-max">
                {tabs.map(tab => {
                  const meta = CAT_META[tab] ?? CAT_META['Other'];
                  const Icon = meta.icon;
                  const count = tab === 'All' ? allItems.length : (grouped[tab]?.length ?? 0);
                  const active = activeTab === tab;
                  return (
                    <button key={tab} onClick={() => setActiveTab(tab)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all whitespace-nowrap
                        ${active
                          ? 'bg-primary text-white border-primary shadow-lg shadow-primary/30'
                          : `${meta.bg} ${meta.color} ${meta.border} hover:opacity-80`}`}>
                      <Icon className="w-3 h-3" />
                      {tab}
                      <span className={`text-[10px] font-bold ml-0.5 ${active ? 'text-white/70' : 'opacity-60'}`}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto overscroll-contain">
      <div className="container mx-auto max-w-4xl px-4 pt-5 pb-28">

        {/* Tap-a-protein hint */}
        {!loading && !error && (grouped['Proteins']?.length ?? 0) > 0 && activeTab !== 'Proteins' && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
            className="mb-4 px-4 py-2.5 rounded-2xl bg-primary/8 border border-primary/20 flex items-center gap-2.5">
            <Sparkles className="w-4 h-4 text-primary shrink-0" />
            <p className="text-xs text-foreground/80">
              <span className="font-bold text-primary">Tap any protein</span> to get AI meal ideas for that sale item
            </p>
          </motion.div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <div className="w-14 h-14 rounded-2xl bg-primary/15 border border-primary/25 flex items-center justify-center">
              <Loader2 className="w-7 h-7 text-primary animate-spin" />
            </div>
            <div className="text-center">
              <p className="font-semibold text-foreground">Loading this week's flyer…</p>
              <p className="text-sm text-muted-foreground mt-1">Fetching live deals from {storeName}</p>
            </div>
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
            <div className="w-14 h-14 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
              <AlertCircle className="w-7 h-7 text-red-400" />
            </div>
            <div>
              <p className="font-semibold text-foreground">Couldn't load flyer</p>
              <p className="text-sm text-muted-foreground mt-1 max-w-xs">{error}</p>
            </div>
            <button onClick={fetchFlyer}
              className="px-5 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold">
              Try Again
            </button>
          </div>
        )}

        {!loading && !error && displayItems.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {displayItems.map((item, i) => {
              const catMeta   = CAT_META[item.cat] ?? CAT_META['Other'];
              const CatIcon   = catMeta.icon;
              const priceCls  = item.price > 0 ? priceColor(item.price) : 'text-muted-foreground bg-muted border-border';
              const inList    = flyerItems.some(f => f.name === item.name);
              const isProtein = item.cat === 'Proteins';
              const unitPrice = isProtein ? parseUnitPrice(item.name, item.price) : null;

              return (
                <div key={`${item.name}-${i}`}
                  onClick={() => {
                    if (isProtein) {
                      setDrawerProtein({ name: item.name, price: item.price, cat: item.cat, unitPrice });
                    }
                  }}
                  className={`rounded-2xl border p-3 flex flex-col gap-2.5 transition-all ${catMeta.bg} ${catMeta.border}
                    ${inList ? 'ring-2 ring-primary/40' : ''}
                    ${isProtein ? 'cursor-pointer hover:shadow-md hover:shadow-primary/10 hover:border-primary/40 active:scale-[0.98]' : ''}`}
                >
                  {/* Category icon + add button */}
                  <div className="flex items-center justify-between">
                    <div className="w-7 h-7 rounded-lg bg-black/20 flex items-center justify-center">
                      <CatIcon className={`w-3.5 h-3.5 ${catMeta.color}`} />
                    </div>
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        inList
                          ? removeFlyerItem(item.name)
                          : addFlyerItem({ name: item.name, price: item.price, cat: item.cat } as FlyerItem);
                      }}
                      className={`w-7 h-7 rounded-full flex items-center justify-center transition-all active:scale-90 ${
                        inList
                          ? 'bg-primary text-primary-foreground shadow-sm shadow-primary/30'
                          : 'bg-black/20 text-foreground/60 hover:bg-primary/80 hover:text-white'
                      }`}
                      title={inList ? 'Remove from list' : 'Add to grocery list'}
                    >
                      {inList ? <Check className="w-3.5 h-3.5" strokeWidth={3} /> : <Plus className="w-3.5 h-3.5" />}
                    </button>
                  </div>

                  {/* Name */}
                  <p className="text-xs font-semibold text-foreground leading-snug line-clamp-3 flex-1">
                    {item.name}
                  </p>

                  {/* Price row */}
                  <div className="flex flex-col gap-1">
                    <div className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border text-xs font-black self-start ${priceCls}`}>
                      <Tag className="w-2.5 h-2.5" />
                      {item.price > 0 ? `$${item.price.toFixed(2)}` : 'Sale'}
                    </div>
                    {unitPrice && (
                      <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg border text-[10px] font-bold text-teal-400 bg-teal-500/10 border-teal-500/25 self-start">
                        {unitPrice.display}
                      </div>
                    )}
                  </div>

                  {/* Protein tap hint */}
                  {isProtein && (
                    <div className="flex items-center gap-1 mt-0.5">
                      <Sparkles className="w-3 h-3 text-primary/70" />
                      <span className="text-[10px] text-primary/70 font-medium">Tap for meal ideas</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* No results for search */}
        {!loading && !error && displayItems.length === 0 && search && (
          <div className="text-center py-16">
            <p className="text-4xl mb-3">🔍</p>
            <p className="font-semibold text-foreground">No deals match "{search}"</p>
            <button onClick={() => setSearch('')} className="text-sm text-primary mt-2">Clear search</button>
          </div>
        )}

        {/* CTA to generate meals from these deals */}
        {!loading && !error && totalDeals > 0 && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-8 rounded-2xl border border-primary/30 bg-primary/8 p-6 text-center">
            <ChefHat className="w-8 h-8 text-primary mx-auto mb-3" />
            <h3 className="font-black text-foreground text-base mb-1">
              Want meals built from these deals?
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              AI turns all {totalDeals} of this week's {storeName} deals into complete recipes with costs and a shopping list.
            </p>
            <button onClick={() => navigate(ROUTE_PATHS.MEAL_PLAN)}
              className="inline-flex items-center gap-2 bg-primary text-white font-bold text-sm px-6 py-3 rounded-xl hover:opacity-90 transition-opacity shadow-lg shadow-primary/30">
              <ChefHat className="w-4 h-4" />
              See My Meal Plan
            </button>
          </motion.div>
        )}
      </div>

      </div>{/* end scrollable content */}

      {/* Meal Ideas Drawer */}
      <AnimatePresence>
        {drawerProtein && (
          <MealIdeasDrawer
            protein={drawerProtein}
            storeName={storeName}
            onClose={() => setDrawerProtein(null)}
            onBuildPlan={() => {
              setDrawerProtein(null);
              navigate(ROUTE_PATHS.MEAL_PLAN);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
